import React from 'react';
import { useParams } from 'react-router-dom';
import LocaleLink from '../components/LocaleLink';
import { useContent } from '../hooks/useContent';
import { useScrollToTop } from '../hooks/useScrollToTop';
import SEO from '../components/SEO';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import {
  PageContainer,
  Container,
  ContentText,
  BulletList,
  staggerContainer,
  staggerItem,
  ItemList,
  ItemCard,
  ItemTitleRow,
  ItemNumber,
  ItemTitle,
  ItemBody,
  StrategicIntelligenceBackLink,
  StrategicIntelligenceBackArrowIcon,
} from '../components/shared/SharedComponents';

const ArticleHeader = styled.header`
  padding: 48px 0 40px;
  border-bottom: 1px solid rgba(29, 51, 78, 0.12);
  margin-bottom: 48px;
`;

const CombinedLabel = styled.div`
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0 8px;
`;

const LabelPart = styled.span`
  color: rgba(29, 51, 78, 0.5);
`;

const LabelSeparator = styled.span`
  color: rgba(29, 51, 78, 0.25);
`;

const LabelCategory = styled.span`
  color: #BF1922;
`;

const ArticleTitle = styled.h1`
  font-size: clamp(28px, 5vw, 36px);
  font-weight: 800;
  color: #1d334e;
  margin: 0 0 16px 0;
  line-height: 1.2;
  letter-spacing: -0.03em;
`;

const ArticleSubtitle = styled.p`
  font-size: clamp(16px, 2vw, 18px);
  color: rgba(29, 51, 78, 0.65);
  margin: 0 0 12px 0;
  line-height: 1.5;
  font-style: italic;
`;


const SeriesBadge = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 20px;
  padding: 12px 16px;
  background: rgba(29, 51, 78, 0.06);
  border-radius: 8px;
  font-size: 13px;
  color: rgba(29, 51, 78, 0.8);
  max-width: 100%;
`;

const SeriesLine = styled.span`
  display: block;
  line-height: 1.5;
`;

const PublishedLine = styled.span`
  display: block;
  font-size: 12px;
  color: rgba(29, 51, 78, 0.5);
`;

const SectionHeading = styled.h2`
  font-size: clamp(20px, 3vw, 24px);
  font-weight: 700;
  color: #1d334e;
  margin: 48px 0 20px 0;
  padding-bottom: 8px;
  line-height: 1.3;

  &:first-of-type {
    margin-top: 0;
  }
`;

const CtaSection = styled.section`
  margin-top: 56px;
  padding: 40px 32px;
  background: linear-gradient(135deg, rgba(29, 51, 78, 0.04) 0%, rgba(191, 25, 34, 0.04) 100%);
  border-radius: 16px;
`;

const CtaHeading = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: #1d334e;
  margin: 0 0 16px 0;
  line-height: 1.3;
`;

const CtaButton = styled(LocaleLink)`
  display: inline-block;
  margin-top: 20px;
  padding: 14px 28px;
  background: linear-gradient(135deg, #BF1922 0%, #8B1419 100%);
  color: #ffffff;
  font-weight: 600;
  font-size: 15px;
  text-decoration: none;
  border-radius: 50px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 14px rgba(191, 25, 34, 0.25);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(191, 25, 34, 0.35);
  }
`;

const ArticleBody = styled.article`
  max-width: 780px;
  margin: 0 auto;
`;


const ArticleSignature = styled.p`
  font-size: 14px;
  font-weight: 600;
  color: rgba(29, 51, 78, 0.55);
  letter-spacing: 0.02em;
  margin: 48px 0 0 0;
  padding-top: 24px;
  border-top: 1px solid rgba(29, 51, 78, 0.1);
`;

const StrategicIntelligenceArticlePage = () => {
  useScrollToTop();
  const { content } = useContent();
  const { categoryId, articleId } = useParams();
  const page = content?.pages?.strategicIntelligence;
  const category = page?.categories?.[categoryId];
  const article = category?.articlePages?.[articleId];
  const pageHeading = page?.articlePageHeading || 'STRATEGIC INTELLIGENCE ANALYSIS';

  if (!category || !article) {
    return (
      <PageContainer>
        <Container>
          <StrategicIntelligenceBackLink to={`/strategic-intelligence/${categoryId}`}>
            <StrategicIntelligenceBackArrowIcon />
            {category?.title || 'Strategic Intelligence'}
          </StrategicIntelligenceBackLink>
          <ContentText as="h1" style={{ fontWeight: 700, color: '#1d334e', marginBottom: 16 }}>
            Article Not Found
          </ContentText>
          <ContentText>
            The requested article could not be found.
          </ContentText>
        </Container>
      </PageContainer>
    );
  }

  const categoryPath = `/strategic-intelligence/${categoryId}`;

  return (
    <>
    <SEO
      title={`${article.title} – APEXGATE GLOBAL`}
      description={article.subtitle || ''}
    />
    <PageContainer>
      <Container>
        <ArticleBody>
          <StrategicIntelligenceBackLink to={categoryPath}>
            <StrategicIntelligenceBackArrowIcon />
            {category.title}
          </StrategicIntelligenceBackLink>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            animate="visible"
          >
            <ArticleHeader as={motion.header} variants={staggerItem}>
              <CombinedLabel>
                <LabelPart>{pageHeading}</LabelPart>
                {article.categoryLabel && (
                  <>
                    <LabelSeparator>|</LabelSeparator>
                    <LabelCategory>{article.categoryLabel}</LabelCategory>
                  </>
                )}
              </CombinedLabel>
              <ArticleTitle>{article.title}</ArticleTitle>
              {article.subtitle && (
                <ArticleSubtitle>{article.subtitle}</ArticleSubtitle>
              )}
              {(article.seriesName || article.seriesTitle || article.publishedIn) && (
                <SeriesBadge>
                  {(article.seriesName || article.seriesTitle) && (
                    <SeriesLine>
                      {[article.seriesName, article.seriesTitle].filter(Boolean).join(' · ')}
                    </SeriesLine>
                  )}
                  {page?.articlePagePublication && article.publishedIn && (
                    <PublishedLine>
                      {page.articlePagePublication} | {article.publishedIn}
                    </PublishedLine>
                  )}
                </SeriesBadge>
              )}
            </ArticleHeader>

            {article.executiveOverview && (
              <ContentText as={motion.div} variants={staggerItem}>
                <SectionHeading>{article.executiveOverview.heading}</SectionHeading>
                {article.executiveOverview.paragraphs?.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </ContentText>
            )}

            {article.sections?.map((section, idx) => (
              <ContentText as={motion.div} key={idx} variants={staggerItem}>
                <SectionHeading>{section.heading}</SectionHeading>
                {section.content
                  ? section.content.map((item, i) => {
                      if (Array.isArray(item)) {
                        return (
                          <BulletList key={i}>
                            {item.map((b, bi) => <li key={bi}>{b}</li>)}
                          </BulletList>
                        );
                      }
                      if (typeof item === 'object' && item?.type === 'titledItems') {
                        return (
                          <ItemList key={i}>
                            {(item.items ?? []).map((ti, tIdx) => (
                              <ItemCard key={tIdx}>
                                {item.numbered ? (
                                  <ItemTitleRow>
                                    <ItemNumber>{tIdx + 1}.</ItemNumber>
                                    <ItemTitle>{ti.title}</ItemTitle>
                                  </ItemTitleRow>
                                ) : (
                                  <ItemTitle style={{ marginBottom: 10 }}>{ti.title}</ItemTitle>
                                )}
                                <ItemBody>
                                  {(ti.content ?? []).map((c, ci) =>
                                    Array.isArray(c) ? (
                                      <BulletList key={ci}>
                                        {c.map((b, bi) => <li key={bi}>{b}</li>)}
                                      </BulletList>
                                    ) : (
                                      <p key={ci}>{c}</p>
                                    )
                                  )}
                                </ItemBody>
                              </ItemCard>
                            ))}
                          </ItemList>
                        );
                      }
                      return <p key={i}>{item}</p>;
                    })
                  : section.paragraphs?.map((p, i) => (
                      <p key={i}>{p}</p>
                    ))
                }
              </ContentText>
            ))}

            {article.meta && (
              <ArticleSignature as={motion.p} variants={staggerItem}>
                {article.meta}
              </ArticleSignature>
            )}

            {article.cta && (
              <CtaSection as={motion.section} variants={staggerItem}>
                <CtaHeading>{article.cta.heading}</CtaHeading>
                {article.cta.paragraphs?.map((p, i) => (
                  <p key={i} style={{ marginBottom: 16, color: 'rgba(29, 51, 78, 0.8)', lineHeight: 1.6 }}>
                    {p}
                  </p>
                ))}
                <CtaButton to={article.cta.buttonLink || '/contact'}>
                  {article.cta.buttonText}
                </CtaButton>
              </CtaSection>
            )}
          </motion.div>
        </ArticleBody>
      </Container>
    </PageContainer>
    </>
  );
};

export default StrategicIntelligenceArticlePage;
